import { useEscapeLayer, useToast } from '@routeback/ui';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { shareFile } from '../lib/share';

/**
 * A link that opens a file (photo or PDF). Photos and copies saved on this
 * phone open in an in-app full-screen viewer with a big Close button: the
 * phone's browser window shows the long storage link at the top, the native
 * app would leave for Safari, and a blob page in a new window has no way back
 * at all. Online PDFs still open the normal way (see inApp).
 */
export function FileOpenLink({ url, title, className, style, children, onOpen, ariaDisabled }: {
  url: string | null | undefined;
  title?: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  onOpen?: () => void;
  ariaDisabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <a
        href={url ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        style={style}
        aria-disabled={ariaDisabled}
        onClick={(e) => {
          onOpen?.();
          if (url && inApp(url)) { e.preventDefault(); setOpen(true); }
        }}
      >{children}</a>
      {open && url && <FileViewer url={url} title={title ?? ''} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Opened inside the app: saved copies (blob:) and every photo. Online PDFs
 * keep the phone's own viewer, which pages through long files (an iframe on
 * iPhone shows only the first page) — the handbook needs that.
 */
export function inApp(url: string): boolean {
  if (url.startsWith('blob:')) return true;
  try { return !/\.pdf$/i.test(new URL(url).pathname); } catch { return true; }
}

/** Full-screen photo/PDF view with a Close button that is always on screen. */
export function FileViewer({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const toast = useToast();
  const [blob, setBlob] = useState<Blob | null>(null);
  useEscapeLayer(true, onClose);
  useEffect(() => {
    let alive = true;
    void fetch(url).then((r) => r.blob()).then((b) => { if (alive) setBlob(b); }).catch(() => {});
    return () => { alive = false; };
  }, [url]);
  // Keep the page behind from scrolling while the viewer is up.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  const pdf = blob?.type === 'application/pdf';
  const share = async () => {
    if (!blob) return;
    const ext = pdf ? 'pdf' : (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
    const name = `${(title || 'document').replace(/[^\w\s.-]/g, '').trim() || 'document'}.${ext}`;
    const how = await shareFile(blob, name, title || undefined);
    if (how === 'downloaded') toast('Saved to your downloads');
  };
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="fv-root" role="dialog" aria-modal="true" aria-label={title || 'File'}>
      <div className="fv-bar">
        <button type="button" className="btn btn-sm fv-close" onClick={onClose}>✕ Close</button>
        <span className="fv-title">{title}</span>
        <button type="button" className="btn btn-sm" disabled={!blob} onClick={() => void share()}>Share</button>
      </div>
      <div className="fv-body">
        {/* Online links here are always photos (inApp), so show them right away. */}
        {!blob ? (url.startsWith('blob:') ? <p className="muted" style={{ margin: 'auto' }}>Loading…</p> : <img src={url} alt="" className="fv-img" />)
          : pdf ? <iframe title={title || 'Document'} src={url} className="fv-frame" />
          : <img src={url} alt="" className="fv-img" />}
      </div>
    </div>,
    document.body,
  );
}
